import { useParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft, ArrowRight, Quote, ArrowUpRight } from 'lucide-react';
import { SEO } from '@/components/common/SEO';
import { PageLoader } from '@/components/ui/Spinner';
import { useCaseStudy, useCaseStudies } from '@/hooks/useCaseStudies';
import { useTranslation } from '@/features/i18n';

export function CaseStudyDetailPage() {
  const { slug } = useParams<{ slug: string }>();
  const { data: caseStudy, isLoading, error } = useCaseStudy(slug || '');
  const { data: allCaseStudies } = useCaseStudies();
  const { t } = useTranslation();

  if (isLoading) {
    return <PageLoader />;
  }

  if (error || !caseStudy) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center bg-[#edf1f5]">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-black mb-4">
            {t.caseStudies.notFound.title}
          </h1>
          <p className="text-[#666] mb-6">
            {t.caseStudies.notFound.description}
          </p>
          <Link to="/case-studies" className="ninito-btn ninito-btn-primary">
            {t.caseStudies.viewAll}
          </Link>
        </div>
      </div>
    );
  }

  const otherCaseStudies = allCaseStudies?.filter((c) => c.slug !== slug).slice(0, 2);

  return (
    <>
      <SEO
        title={caseStudy.seo_title || caseStudy.title}
        description={caseStudy.seo_description || caseStudy.problem_description || ''}
        keywords={caseStudy.seo_keywords || ''}
        ogImage={caseStudy.og_image_url || caseStudy.cover_image_url || ''}
      />

      {/* Header - ninito style */}
      <header className="bg-white pt-32 pb-16 lg:pt-40 lg:pb-20">
        <div className="ninito-container">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <Link
              to="/case-studies"
              className="inline-flex items-center text-[#666] hover:text-black transition-colors mb-8"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              {t.caseStudies.backToCaseStudies}
            </Link>

            <div className="flex flex-wrap items-center gap-3 mb-6">
              {caseStudy.industry && (
                <span className="px-3 py-1 rounded-full bg-[#0078ff]/10 text-[#0078ff] text-sm font-medium">
                  {caseStudy.industry}
                </span>
              )}
              {caseStudy.project_duration && (
                <span className="px-3 py-1 rounded-full bg-black/5 text-[#666] text-sm font-medium">
                  {caseStudy.project_duration}
                </span>
              )}
            </div>

            <h1 className="text-[32px] md:text-[40px] lg:text-[52px] font-semibold text-black leading-[1.1] tracking-[-0.02em]">
              {caseStudy.title}
            </h1>

            <p className="mt-4 text-xl text-[#0078ff] font-medium">
              {caseStudy.client_name}
            </p>
          </motion.div>
        </div>
      </header>

      {/* Results highlight - ninito style */}
      {caseStudy.results_json && caseStudy.results_json.length > 0 && (
        <section className="py-16 bg-black">
          <div className="ninito-container">
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-8">
              {caseStudy.results_json.map((result, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  className="text-center"
                >
                  <div className="text-[40px] lg:text-[48px] font-bold text-white tracking-tight">
                    {result.metric}
                  </div>
                  <div className="mt-2 text-white/50">{result.label}</div>
                </motion.div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Cover image - ninito style */}
      {caseStudy.cover_image_url && (
        <div className="max-w-[1100px] mx-auto px-5 md:px-10 py-12">
          <motion.img
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.2 }}
            src={caseStudy.cover_image_url}
            alt={caseStudy.title}
            className="w-full aspect-[16/9] object-cover rounded-[28px]"
          />
        </div>
      )}

      {/* Content - ninito style */}
      <section className="ninito-section bg-white">
        <div className="max-w-[800px] mx-auto px-5 md:px-10">
          {/* Problem */}
          {caseStudy.problem_description && (
            <div className="mb-12">
              <h2 className="text-2xl font-semibold text-black tracking-tight mb-4">
                {t.caseStudies.challenge}
              </h2>
              <p className="text-lg text-[#666] leading-relaxed">
                {caseStudy.problem_description}
              </p>
            </div>
          )}

          {/* Solution */}
          {caseStudy.solution_description && (
            <div className="mb-12">
              <h2 className="text-2xl font-semibold text-black tracking-tight mb-4">
                {t.caseStudies.solution}
              </h2>
              <p className="text-lg text-[#666] leading-relaxed">
                {caseStudy.solution_description}
              </p>
            </div>
          )}

          {/* Technologies - ninito style */}
          {caseStudy.technologies_used && caseStudy.technologies_used.length > 0 && (
            <div className="mb-12">
              <h2 className="text-2xl font-semibold text-black tracking-tight mb-4">
                {t.caseStudies.technologies}
              </h2>
              <div className="flex flex-wrap gap-2">
                {caseStudy.technologies_used.map((tech) => (
                  <span
                    key={tech}
                    className="px-4 py-2 rounded-full bg-[#edf1f5] text-[#666] text-sm font-medium"
                  >
                    {tech}
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* Testimonial - ninito style */}
          {caseStudy.testimonial_quote && (
            <div className="ninito-card bg-[#edf1f5] mb-12">
              <div className="flex items-start">
                <div className="w-12 h-12 rounded-2xl bg-[#0078ff]/10 flex items-center justify-center flex-shrink-0 mr-5">
                  <Quote className="w-6 h-6 text-[#0078ff]" />
                </div>
                <div>
                  <blockquote className="text-xl text-black italic leading-relaxed mb-4">
                    "{caseStudy.testimonial_quote}"
                  </blockquote>
                  <div className="font-semibold text-black">
                    {caseStudy.testimonial_author}
                  </div>
                  {caseStudy.testimonial_role && (
                    <div className="text-sm text-[#666] mt-1">
                      {caseStudy.testimonial_role}, {caseStudy.client_name}
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Gallery - ninito style */}
          {caseStudy.gallery_images && caseStudy.gallery_images.length > 0 && (
            <div>
              <h2 className="text-2xl font-semibold text-black tracking-tight mb-6">
                {t.caseStudies.gallery}
              </h2>
              <div className="grid sm:grid-cols-2 gap-5">
                {caseStudy.gallery_images.map((image, index) => (
                  <img
                    key={index}
                    src={image}
                    alt={`${caseStudy.title} - bilde ${index + 1}`}
                    className="w-full aspect-[4/3] object-cover rounded-2xl"
                  />
                ))}
              </div>
            </div>
          )}
        </div>
      </section>

      {/* Related case studies - ninito style */}
      {otherCaseStudies && otherCaseStudies.length > 0 && (
        <section className="ninito-section bg-[#edf1f5]">
          <div className="ninito-container">
            <h2 className="text-2xl font-semibold text-black tracking-tight mb-10">
              {t.caseStudies.moreCaseStudies}
            </h2>
            <div className="grid md:grid-cols-2 gap-7">
              {otherCaseStudies.map((study, index) => (
                <motion.article
                  key={study.id}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  viewport={{ once: true }}
                >
                  <Link to={`/case-studies/${study.slug}`} className="block group">
                    <div className="ninito-card h-full p-0 overflow-hidden ninito-hover-lift">
                      {study.cover_image_url && (
                        <div className="aspect-[16/9] overflow-hidden rounded-t-[28px]">
                          <img
                            src={study.cover_image_url}
                            alt={study.title}
                            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                          />
                        </div>
                      )}
                      <div className="p-6 lg:p-8">
                        <h3 className="text-xl font-semibold text-black group-hover:text-[#0078ff] transition-colors">
                          {study.title}
                        </h3>
                        <p className="mt-2 text-[#0078ff] font-medium">
                          {study.client_name}
                        </p>
                        <div className="mt-4 flex items-center text-[#666] text-sm font-medium">
                          Les mer
                          <ArrowUpRight className="w-4 h-4 ml-1 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
                        </div>
                      </div>
                    </div>
                  </Link>
                </motion.article>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* CTA - ninito dark style */}
      <section className="ninito-cta">
        <div className="relative ninito-container">
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-[32px] md:text-[40px] lg:text-[48px] font-semibold text-white tracking-[-0.02em]"
          >
            {t.caseStudies.cta.ready}
          </motion.h2>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.1 }}
            className="mt-5 text-lg md:text-xl text-[#999] max-w-[600px] mx-auto"
          >
            {t.caseStudies.cta.readyDesc}
          </motion.p>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.2 }}
            className="mt-10"
          >
            <Link to="/contact" className="ninito-btn ninito-btn-white ninito-btn-lg group">
              {t.caseStudies.cta.button}
              <ArrowRight className="w-5 h-5 group-hover:translate-x-0.5 transition-transform" />
            </Link>
          </motion.div>
        </div>
      </section>
    </>
  );
}
